import React, { Fragment } from 'react'
import { useState, useEffect, useCallback, useUniqueId } from 'sb-hooks'
import { Message } from 'sb-react-intl'

import VisuallyHidden from 'components/ui/VisuallyHidden/VisuallyHidden'


export type CheckboxLogicProps = {
  ariaLabel: string | ReactIntlMessage
  checked?: boolean // initial checkbox state, can be changed after mounting to override current state
  disabled?: boolean
  onChange?: (checked: boolean) => void
}

const CheckboxLogic: React.FunctionComponent<CheckboxLogicProps> = (props) => {
  const { children, ariaLabel, checked = false, disabled = false, onChange } = props

  const uniqueId = useUniqueId()
  const [ isChecked, setCheckedStatus ] = useState(checked)

  useEffect(() => {
    setCheckedStatus((isChecked) => checked !== isChecked ? checked : isChecked)
  }, [ checked ])

  const handleClick = useCallback(() => {
    if (!disabled) {
      setCheckedStatus((value) => {
        if (typeof onChange === 'function') {
          onChange(!value)
        }

        return !value
      })
    }
  }, [ disabled, onChange ])

  const id = `checkBoxLogic_${uniqueId}`
  let content

  if (typeof children === 'function') {
    content = children({ isChecked })
  }
  else {
    content = children
  }

  return React.cloneElement(
    content,
    {
      type: 'button',
      role: 'checkbox',
      'aria-checked': isChecked,
      'aria-disabled': disabled,
      'aria-labelledby': id,
      onClick: handleClick,
      render: () => (
        <Fragment>
          <VisuallyHidden>
            <Message id={id} text={ariaLabel} />
          </VisuallyHidden>
          {content.props.children}
        </Fragment>
      ),
    }
  )
}


export default CheckboxLogic
